import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CardHeader,
  CardContent,
  Divider,
  Paper,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';

import { useParams } from 'react-router-dom';
import { DetailPageHeader } from '../../components/layout/DetailPageHeader';
import { Add, Cancel, Engineering } from '@mui/icons-material';
import { capitalizeWords, formatDate } from '../../utils/helper';
import { GETCopperPipingRequestById } from '../../services/copperPiping';
import { GETQuotesForEnquiry, UpdateEnquiryStatus } from '../../services/requests';
import { CustomerRequestDetails, QuotationsCard } from '../Amc/AMCRequest';
import AssignTechnicianModal from '../Booking/actions/TechnicianModal';
import AddQuotation from '../Amc/AddQuotation';
import useSnackbar from '../../hooks/useSnackbar';

interface ACTypeDetail {
  type: string;
  quantity: number;
}

interface CopperPipingDetails {
  propertyType?: string;
  acTypes?: ACTypeDetail[];
  outdoorUnitLocation?: string;
  pipeLength?: number;
  additionalNotes?: string;
  images?: string[];
}

export interface CopperPipingEnquiry {
  _id: string;
  enquiryId: string;
  status: string;
  noOfAc: number;
  cancelReason?: string;
  createdAt: string;
  updatedAt: string;

  schedule: {
    slot: string | null;
    date: string | null;
  };

  addressDetails: {
    house: string;
    street: string;
    city: string;
    state: string;
    zipcode: string;
    saveAs: string;
    landmark: string;
  };

  user_info: {
    _id: string;
    name: string;
    phoneNumber: string;
    email: string;
    countryCode: string;
    profilePhoto: string;
    type?: string;
  };

  technician_info: {
    _id: string;
    name: string;
    phoneNumber: string;
    email: string;
    countryCode: string;
    profilePhoto: string;
    type?: string;
  };

  copperPipingDetails: CopperPipingDetails;
}

const formatEnumLabel = (value?: string) =>
  value ? capitalizeWords(value.replaceAll('_', ' ')) : 'N/A';

const composeAddress = (
  address?: CopperPipingEnquiry['addressDetails'],
): string => {
  if (!address) return 'N/A';
  const parts = [
    address.house,
    address.street,
    address.landmark,
    address.city,
    address.state,
    address.zipcode,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : 'N/A';
};

const CopperPipingRequest = () => {
  const { id } = useParams();
  const [request, setRequest] = useState<CopperPipingEnquiry | null>(null);
  const [quotes, setQuotes] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openAssignModal, setOpenAssignModal] = useState(false);
  const [openQuoteModal, setOpenQuoteModal] = useState(false);
  const [openRejectDialog, setOpenRejectDialog] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  useEffect(() => {
    if (id) {
      fetchRequest(id);
    }
  }, [id]);

  useEffect(() => {
    const enquiryId = request?._id;
    if (!enquiryId) {
      setQuotes([]);
      return;
    }
    GETQuotesForEnquiry(enquiryId).then(setQuotes);
  }, [request?._id]);

  async function fetchRequest(enquiryId: string) {
    try {
      setLoading(true);
      setError('');

      const data = await GETCopperPipingRequestById(enquiryId);

      setRequest(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to load request');
    } finally {
      setLoading(false);
    }
  }

  const handleRejectRequest = async () => {
    // Backend resolves the enquiry by its human-readable code, not Mongo _id
    if (!request?.enquiryId) return;

    try {
      setRejecting(true);
      await UpdateEnquiryStatus(request.enquiryId, 'CANCELLED', rejectReason.trim());
      showSnackbar('Copper piping request rejected', 'success');
      setOpenRejectDialog(false);
      setRejectReason('');
      if (id) {
        fetchRequest(id);
      }
    } catch (err) {
      showSnackbar(
        err instanceof Error ? err.message : 'Failed to reject request',
        'error',
      );
    } finally {
      setRejecting(false);
    }
  };

  const customer = useMemo(() => {
    if (!request) return null;

    return {
      _id: request.user_info?._id ?? '',

      fullName: request.user_info?.name ?? '',

      contactNumber: request.user_info?.phoneNumber ?? '',

      customerType: request.user_info?.type || 'N/A',

      placeType: request.copperPipingDetails?.propertyType || 'N/A',

      address: composeAddress(request.addressDetails),

      totalAC: request.noOfAc ?? 0,

      inspectionDate: formatDate(request.schedule?.date),

      inspectionTime: request.schedule?.slot || 'N/A',

      inspectionAddress: composeAddress(request.addressDetails),

      inspector: request.technician_info?.name || '',

      cancelReason: request.cancelReason || '',
    };
  }, [request]);

  const isCancelled = request?.status === 'CANCELLED';

  if (loading) {
    return (
      <>
        <DetailPageHeader title={'Request Details'} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
          }}
        >
          <CircularProgress sx={{ color: '#E31E25' }} />
        </Box>
      </>
    );
  }

  if (error && !request) {
    return (
      <>
        <DetailPageHeader title={'Request Details'} />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 2,
            minHeight: '50vh',
          }}
        >
          <Typography color="error">{error}</Typography>
          <Button
            variant="outlined"
            color="error"
            onClick={() => id && fetchRequest(id)}
          >
            Retry
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <DetailPageHeader title={'Request Details'} />
      <Box
        sx={{
          backgroundColor: '#1C1C1C',
          color: '#fff',
          borderRadius: 2,
          m: 3,
        }}
      >
        <CustomerRequestDetails
          customer={customer}
          status={request?.status ?? ''}
          type="REGULAR"
        />

        <CopperPipingDetailsCard
          details={request?.copperPipingDetails ?? {}}
        />

        <QuotationsCard quotes={quotes} />
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          m: 3,
        }}
      >
        {/* Left Button */}
        <Button
          variant="outlined"
          sx={{
            color: '#D32F2F',
            borderColor: '#D32F2F',
            px: 2.5,
            py: 1.2,
            borderRadius: '14px',
            '&:hover': {
              borderColor: '#B71C1C',
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
            },
          }}
          startIcon={<Cancel />}
          disabled={!request?.enquiryId || isCancelled}
          onClick={() => setOpenRejectDialog(true)}
        >
          Reject Request
        </Button>

        {/* Right Buttons */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            startIcon={<Add />}
            sx={{
              color: '#fff',
              backgroundColor: '#E31E25',
              '&:hover': {
                backgroundColor: '#C62828',
              },
              padding: '10px 20px',
              borderRadius: '14px',
            }}
            disabled={!request}
            onClick={() => setOpenQuoteModal(true)}
          >
            Add Quotation
          </Button>

          <Button
            startIcon={<Engineering />}
            sx={{
              color: '#fff',
              backgroundColor: '#E31E25',
              '&:hover': {
                backgroundColor: '#C62828',
              },
              padding: '10px 20px',
              borderRadius: '14px',
            }}
            disabled={!request?._id}
            onClick={() => setOpenAssignModal(true)}
          >
            Assign Inspector
          </Button>
        </Box>
      </Box>

      {/* Reject confirmation */}
      <Dialog
        open={openRejectDialog}
        onClose={() => !rejecting && setOpenRejectDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Reject Copper Piping Request</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to reject this copper piping request? This
            will mark the enquiry as CANCELLED.
          </Typography>
          <TextField
            label="Cancellation reason"
            placeholder="Let the team know why this request is being rejected"
            fullWidth
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenRejectDialog(false)}
            disabled={rejecting}
          >
            Keep Request
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRejectRequest}
            disabled={rejecting}
          >
            {rejecting ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign inspector */}
      {request?._id && (
        <AssignTechnicianModal
          isOpen={openAssignModal}
          onClose={() => setOpenAssignModal(false)}
          bookingId={request._id}
          isEnquiry={true}
          onReload={() => id && fetchRequest(id)}
        />
      )}

      <AddQuotation
        open={openQuoteModal}
        onClose={() => setOpenQuoteModal(false)}
        id={id || ''}
        source="COPPER_PIPING"
        enquiryId={request?._id}
        onSaved={() => {
          if (id) fetchRequest(id);
          if (request?._id) GETQuotesForEnquiry(request._id).then(setQuotes);
        }}
      />

      {SnackbarComponent}
    </>
  );
};

export default CopperPipingRequest;

/* Figma-style details section: green label rows on dark bands */
const CopperPipingDetailsCard = ({
  details,
}: {
  details: CopperPipingDetails;
}) => {
  const acTypes = details.acTypes ?? [];
  const images = details.images ?? [];
  const hasDetails = Object.keys(details).length > 0;

  // "Property Type" is intentionally omitted here — it's the same value
  // already shown once above as "Place Type" in the shared customer detail
  // card, and repeating it here made it appear twice on screen.
  const infoRows: { label: string; value: string }[] = [
    {
      label: 'Outdoor Unit Location',
      value: formatEnumLabel(details.outdoorUnitLocation),
    },
    {
      label: 'Pipe Length',
      value:
        details.pipeLength !== undefined && details.pipeLength !== null
          ? `${details.pipeLength} ft`
          : 'N/A',
    },
  ];

  return (
    <Box sx={{ color: '#fff' }}>
      <CardHeader
        title="Copper Piping Details"
        titleTypographyProps={{
          fontWeight: 600,
          fontSize: 18,
        }}
      />

      <Divider sx={{ borderColor: '#565656' }} />

      <CardContent sx={{ backgroundColor: '#232323', p: 3 }}>
        {!hasDetails ? (
          <Typography variant="body2" sx={{ color: '#9e9e9e' }}>
            No copper piping details provided
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {acTypes.map((item, index) => (
                <Paper
                  key={`${item.type}-${index}`}
                  elevation={0}
                  sx={{
                    backgroundColor: '#2E2E2E',
                    borderRadius: 1,
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#0BE600',
                      fontWeight: 600,
                      width: '20%',
                      minWidth: 120,
                    }}
                  >
                    {formatEnumLabel(item.type)}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {item.quantity} Unit{item.quantity > 1 ? 's' : ''}
                  </Typography>
                </Paper>
              ))}

              {infoRows.map((row) => (
                <Paper
                  key={row.label}
                  elevation={0}
                  sx={{
                    backgroundColor: '#2E2E2E',
                    borderRadius: 1,
                    px: 2,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#9e9e9e',
                      width: '20%',
                      minWidth: 120,
                    }}
                  >
                    {row.label}
                  </Typography>

                  <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                    {row.value}
                  </Typography>
                </Paper>
              ))}
            </Box>

            {details.additionalNotes && (
              <>
                <Divider sx={{ my: 3, borderColor: '#565656' }} />

                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: '#9e9e9e' }}
                >
                  Additional Notes
                </Typography>

                <Typography variant="body2" sx={{ color: '#ddd' }}>
                  {details.additionalNotes}
                </Typography>
              </>
            )}

            {images.length > 0 && (
              <>
                <Divider sx={{ my: 3, borderColor: '#565656' }} />

                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: '#9e9e9e' }}
                >
                  Images
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                  {images.map((url, index) => (
                    <Box
                      key={`${url}-${index}`}
                      component="img"
                      src={url}
                      alt={`Copper piping ${index + 1}`}
                      onClick={() => window.open(url, '_blank')}
                      sx={{
                        width: 96,
                        height: 96,
                        objectFit: 'cover',
                        borderRadius: 1,
                        border: '1px solid #3a3a3a',
                        cursor: 'pointer',
                      }}
                    />
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>

      <Divider sx={{ borderColor: '#565656' }} />
    </Box>
  );
};



